/**
 * 2026-04-25 Phase 32-A.4 — 제련 모달 (간소화 재작성).
 *
 * 설계 원칙:
 *   1. 한 화면, 버튼 1~2개.
 *   2. 시퀀스 슬롯의 색이 곧 규칙서 — 텍스트 설명 없음.
 *      꼬리 SS → 금테 / 꼬리 FF → 피테 / 꼬리 단일 S/F → 옅은 색.
 *   3. 진입 시 비용 없음 (세션만 생성). "망치질" 을 눌러야 실제 소모.
 *   4. 꼬리 SS/FF 에 도달하면 유저 입력 없이 자동 마무리 (큰 이펙트).
 *   5. 4 타 소진 시 자동 마무리.
 *   6. 상황어 버튼: "그만둔다"(진입 전) / "이대로 마무리한다"(1타 후+).
 *   7. 심장박동·호흡 BGM 제거 — 타격 순간 단발 사운드만.
 */
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { tokens } from "@/art/tokens";
import { PrimaryButton } from "@/components/Button/PrimaryButton";
import {
    refineStart, refineContinue, refineStop,
    type RefineStepResult, type RefineFinalResult,
} from "@/api/endpoints/forge";
import type { InventoryItemView } from "@/api/types";
import { describeApiError } from "@/utils/errorMessages";
import { ForgeOutcomeBurst } from "@/components/forge/ForgeOutcomeBurst";
import { BlackoutFlash } from "@/components/forge/BlackoutFlash";
import { AnvilStrike } from "@/components/forge/AnvilStrike";
import { resolveEquipmentAuraColor } from "@/components/forge/EquipmentAura";
import { markForgeShimmer } from "@/components/forge/useForgeShimmer";
import { equipmentIcon } from "@/components/forge/equipmentIcon";
import { countByItemId } from "@/utils/forge";

interface Props {
    runId: string;
    item: InventoryItemView;
    inventoryItems?: InventoryItemView[];
    onClose: () => void;
    onComplete: (result: RefineFinalResult) => void;
}

type Phase = "loading" | "active" | "striking" | "blackout" | "bursting" | "final" | "error";

const FIRST_REFINE_KEY = "rpg.refine.firstHintSeen";

export function RefineModal({ runId, item, inventoryItems, onClose, onComplete }: Props) {
    const [phase, setPhase] = useState<Phase>("loading");
    const [step, setStep] = useState<RefineStepResult | null>(null);
    const [final, setFinal] = useState<RefineFinalResult | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [pendingStep, setPendingStep] = useState<RefineStepResult | null>(null);
    const [pendingFinal, setPendingFinal] = useState<RefineFinalResult | null>(null);
    // 2026-04-25: 모달 내부에서 strike 당 consumed 한 제련석 누적.
    //   parent 의 inventoryItems 는 FinalStage 에서 refresh 되므로, 진행 중엔 로컬에서 차감해서 표시.
    const [localStoneConsumed, setLocalStoneConsumed] = useState(0);
    // 첫 제련 여부 — 슬롯 위 한 줄 힌트 노출용
    const [showFirstHint] = useState(() => !localStorage.getItem(FIRST_REFINE_KEY));

    const itemName = item.definition?.name ?? "장비";

    // 진입 시 세션 자동 생성 (비용 없음)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await refineStart(runId, item.instanceId);
                if (cancelled) return;
                setStep(res);
                setPhase("active");
            } catch (e) {
                if (!cancelled) {
                    const d = describeApiError(e);
                    setErrorMsg(`${d.title}: ${d.message}`);
                    setPhase("error");
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const finalize = (fin: RefineFinalResult) => {
        // 첫 제련 완료 표시
        localStorage.setItem(FIRST_REFINE_KEY, "1");
        setFinal(fin);
        if (!fin.destroyed && (fin.outcome === "SUCCESS" || fin.outcome === "CRIT_SUCCESS") && fin.item) {
            markForgeShimmer(fin.item.instanceId, "refine");
        }
        // CRIT 는 항상 극적 — blackout → bursting. SUCCESS/FAIL 은 bursting 바로.
        const isCrit = fin.outcome === "CRIT_SUCCESS" || fin.outcome === "CRIT_FAIL";
        setPhase(isCrit ? "blackout" : "bursting");
        onComplete(fin);
    };

    /** 꼬리 2자리로 자동 종료 여부 판정. */
    const shouldAutoFinalize = (sequence: string, canContinue: boolean): boolean => {
        if (!canContinue) return true;  // 4타 소진
        if (sequence.length >= 2) {
            const tail = sequence.slice(-2);
            if (tail === "SS" || tail === "FF") return true;  // 대성공·대실패
        }
        return false;
    };

    const strike = async () => {
        if (!step || busy) return;
        setBusy(true);
        setPendingStep(null);
        setPendingFinal(null);
        setPhase("striking");
        try {
            const res = await refineContinue(runId, step.sessionId);
            setPendingStep(res);
            setLocalStoneConsumed(c => c + 1);
            // 꼬리 SS/FF 또는 4타 → 자동 /stop
            if (shouldAutoFinalize(res.sequence, res.canContinue)) {
                const fin = await refineStop(runId, step.sessionId);
                setPendingFinal(fin);
            }
        } catch (e) {
            const d = describeApiError(e);
            setErrorMsg(`${d.title}: ${d.message}`);
            setPhase("error");
            setBusy(false);
        }
    };

    const onStrikeAnimEnd = () => {
        setBusy(false);
        if (pendingFinal) {
            setStep(pendingStep);
            finalize(pendingFinal);
            setPendingStep(null);
            setPendingFinal(null);
            return;
        }
        if (pendingStep) {
            setStep(pendingStep);
            setPhase("active");
            setPendingStep(null);
        }
    };

    const stopNow = async () => {
        if (!step || busy) return;
        setBusy(true);
        try {
            const fin = await refineStop(runId, step.sessionId);
            finalize(fin);
        } catch (e) {
            const d = describeApiError(e);
            setErrorMsg(`${d.title}: ${d.message}`);
            setPhase("error");
            setBusy(false);
        }
    };

    const sequence = step?.sequence ?? "";
    const stepsDone = sequence.length;
    const auraColor = resolveEquipmentAuraColor(item.enhancement, item.engraveTier);

    // 2026-04-25: 안전 닫기 — 연출(striking/blackout/bursting) 중에는 X.
    //   active/loading/error/final 단계에서만 ESC, 백드롭 클릭으로 닫기 허용.
    const canCloseNow = phase === "active" || phase === "loading" || phase === "error" || phase === "final";
    useEffect(() => {
        if (!canCloseNow) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [canCloseNow, onClose]);

    return (
        <div
            style={overlayStyle}
            onClick={canCloseNow ? onClose : undefined}
        >
            <motion.div
                onClick={(e) => e.stopPropagation()}
                style={modalStyle}
                animate={phase === "striking" && pendingStep !== null
                    ? {
                        x: [0, -14, 12, -10, 8, -5, 3, 0],
                        y: [0, -6, 4, -3, 2, 0, 0, 0],
                    }
                    : { x: 0, y: 0 }}
                transition={phase === "striking" && pendingStep !== null
                    ? { duration: 0.56, ease: "easeOut", delay: 0.62 }
                    : { type: "tween", duration: 0.2 }}
            >
                {/* 우상단 닫기 — 연출 중엔 비활성. */}
                {canCloseNow && (
                    <button style={closeButton} onClick={onClose} aria-label="닫기">✕</button>
                )}

                <h2 style={titleStyle}>제련 · {itemName}</h2>

                {phase === "loading" && (
                    <div style={loadingStyle}>준비하는 중…</div>
                )}

                {phase === "error" && (
                    <div style={errorStyle}>
                        <p>{errorMsg}</p>
                        <button style={ghostButton} onClick={onClose}>닫기</button>
                    </div>
                )}

                {(phase === "active" || phase === "striking") && step && (
                    <MainStage
                        item={item}
                        step={step}
                        sequence={sequence}
                        stepsDone={stepsDone}
                        auraColor={auraColor}
                        inventoryItems={inventoryItems}
                        localStoneConsumed={localStoneConsumed}
                        busy={busy}
                        showFirstHint={showFirstHint}
                        onStrike={strike}
                        onStop={stopNow}
                        onClose={onClose}
                    />
                )}

                {phase === "final" && final && (
                    <FinalStage final={final} onClose={onClose} />
                )}
            </motion.div>

            {phase === "striking" && step && (
                <AnvilStrike
                    stepSuccess={pendingStep?.stepSuccess ?? null}
                    stepIndex={stepsDone + 1}
                    onComplete={onStrikeAnimEnd}
                />
            )}

            {phase === "blackout" && (
                <BlackoutFlash onComplete={() => setPhase("bursting")} />
            )}

            {phase === "bursting" && final && (
                <ForgeOutcomeBurst
                    outcome={final.outcome}
                    kind="refine"
                    accentColor={final.item ? resolveEquipmentAuraColor(final.item.enhancement, final.item.engraveTier) : undefined}
                    destroyed={final.destroyed}
                    onComplete={() => setPhase("final")}
                />
            )}
        </div>
    );
}

// ==================================================================
// MainStage — 장비 + 슬롯 + 비용 + 버튼
// ==================================================================

function MainStage({ item, step, sequence, stepsDone, auraColor, inventoryItems, localStoneConsumed, busy, showFirstHint, onStrike, onStop, onClose }: {
    item: InventoryItemView;
    step: RefineStepResult;
    sequence: string;
    stepsDone: number;
    auraColor: string | undefined;
    inventoryItems?: InventoryItemView[];
    localStoneConsumed: number;
    busy: boolean;
    showFirstHint: boolean;
    onStrike: () => void;
    onStop: () => void;
    onClose: () => void;
}) {
    const icon = equipmentIcon(item);
    const cost = step.nextStepCost;
    const successRate = step.nextStepOddsPct ?? 0;

    // 2026-04-25: 재료 보유 여부 체크 — 세션 중엔 부모 inventoryItems 가 갱신되지 않으므로
    //   로컬 consumed 카운트로 차감. 정확한 예상 가용량을 표시.
    const rawStoneCount = cost ? countByItemId(inventoryItems, cost.refineStoneId) : 0;
    const stoneCount = Math.max(0, rawStoneCount - localStoneConsumed);
    const stoneOk = !cost || stoneCount >= cost.refineStoneCount;

    // 버튼 로직 — 상황에 따라 2개 또는 1개
    const canStrike = step.canContinue && stoneOk;
    const canStop = step.canStop;  // backend: stepIndex >= 1

    return (
        <div style={stageStyle}>
            {/* 장비 아이콘 + 현재 단계 */}
            <div style={itemBadgeWrap}>
                <div style={{
                    ...itemBadge,
                    boxShadow: auraColor
                        ? `0 0 44px ${auraColor}66, 0 0 16px ${auraColor}44`
                        : `0 0 20px rgba(255,255,255,0.08)`,
                }}>
                    <span style={{ fontSize: 54 }}>{icon}</span>
                </div>
                <div style={enhancementBadge}>+{item.enhancement}</div>
            </div>

            {/* 첫 경험 한 줄 힌트 — 풀어 씀 (모호 용어 회피) */}
            {showFirstHint && stepsDone === 0 && (
                <div style={firstHintStyle}>마지막 두 칸이 결과를 결정합니다</div>
            )}
            {/* 2026-04-25: +9 → +10 케이스 — 성공만 해도 만렙. 대성공 노릴 이유 없음. */}
            {item.enhancement === 9 && stepsDone === 0 && (
                <div style={maxBeforeHintStyle}>
                    한 번만 성공해도 만렙(+10). 대성공 무리할 필요 없습니다
                </div>
            )}
            {/* 2026-04-25: 마지막 한 칸이 남았을 때 — 다음 한 번이 끝. */}
            {stepsDone === 3 && step.canContinue && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4 }}
                    style={lastChanceHintStyle}
                >
                    이번이 마지막 한 번 — 결과로 굳어집니다
                </motion.div>
            )}

            {/* 시퀀스 슬롯 */}
            <SequenceSlots sequence={sequence} stepsDone={stepsDone} />

            {/* 비용·성공률 — 버튼 바로 위, 한 줄. 보유 < 필요 면 색으로 표시. */}
            {step.canContinue && cost && (
                <div style={costLineStyle}>
                    <span style={{ color: stoneOk ? "inherit" : tokens.color.burgundy }}>
                        {cost.refineStoneId === "material.refine_stone_rare" ? "💎 별철" : "🪨 제련석"} {cost.refineStoneCount}
                        <span style={{ opacity: 0.55, marginLeft: 4, fontSize: 12 }}>(보유 {stoneCount})</span>
                    </span>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span>🪙 {cost.gold.toLocaleString()}</span>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span style={{ color: tokens.color.brass }}>{successRate}%</span>
                </div>
            )}

            {/* 액션 */}
            <div style={actionColumn}>
                {step.canContinue && (
                    <PrimaryButton onClick={onStrike} disabled={busy || !canStrike}>
                        {canStrike ? "🔨 망치질" : "🔨 재료 부족"}
                    </PrimaryButton>
                )}
                {canStop
                    ? (
                        <button style={ghostButton} onClick={onStop} disabled={busy}>
                            이대로 마무리한다
                        </button>
                    )
                    : (
                        <button style={ghostButton} onClick={onClose} disabled={busy}>
                            그만둔다
                        </button>
                    )}
            </div>
        </div>
    );
}

// ==================================================================
// SequenceSlots — 규칙서이자 UI
// ==================================================================

function SequenceSlots({ sequence, stepsDone }: { sequence: string; stepsDone: number }) {
    // 꼬리 2자리 판정 — 슬롯 색 결정
    const highlightIndices = useMemo(() => {
        const n = sequence.length;
        if (n < 2) return new Set<number>();
        const tail = sequence.slice(-2);
        if (tail === "SS" || tail === "FF") return new Set([n - 2, n - 1]);
        return new Set<number>();
    }, [sequence]);
    const tailKind = useMemo(() => {
        if (sequence.length < 2) return null;
        const tail = sequence.slice(-2);
        if (tail === "SS") return "crit_success" as const;
        if (tail === "FF") return "crit_fail" as const;
        return null;
    }, [sequence]);

    return (
        <div style={slotsRow}>
            {[0, 1, 2, 3].map(i => {
                const ch = sequence[i];
                const isFilled = ch !== undefined;
                const isLast = i === stepsDone - 1;
                const isHighlighted = highlightIndices.has(i);

                let bg: string = "transparent";
                let border: string = `1px solid ${tokens.color.brass}33`;
                let glow: string = "none";
                let symbolColor: string = tokens.color.parchment;
                let symbol: string | null = null;

                if (isFilled) {
                    if (ch === "S") {
                        symbol = "✦";
                        symbolColor = tokens.color.brass;
                        bg = `${tokens.color.brass}12`;
                        border = `1px solid ${tokens.color.brass}88`;
                    } else {
                        symbol = "✕";
                        symbolColor = "#988";
                        bg = "rgba(255,255,255,0.03)";
                        border = `1px solid #88888844`;
                    }
                }

                if (isHighlighted && tailKind === "crit_success") {
                    border = `2px solid ${tokens.color.brass}`;
                    glow = `0 0 22px ${tokens.color.brass}88, inset 0 0 14px ${tokens.color.brass}44`;
                    symbolColor = tokens.color.brass;
                }
                if (isHighlighted && tailKind === "crit_fail") {
                    border = `2px solid ${tokens.color.burgundy}`;
                    glow = `0 0 22px ${tokens.color.burgundy}88, inset 0 0 14px ${tokens.color.burgundy}44`;
                    symbolColor = tokens.color.burgundy;
                }

                return (
                    <motion.div
                        key={i}
                        animate={isLast ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                        transition={{ duration: 0.35 }}
                        style={{
                            width: 58,
                            height: 68,
                            borderRadius: 10,
                            background: bg,
                            border,
                            boxShadow: glow,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 26,
                            color: symbolColor,
                            fontFamily: "serif",
                            fontWeight: 700,
                            transition: "box-shadow 0.35s ease, border 0.3s ease",
                        }}
                    >
                        {symbol ?? ""}
                    </motion.div>
                );
            })}
        </div>
    );
}

// ==================================================================
// FinalStage — 결과 + 닫기
// ==================================================================

function FinalStage({ final, onClose }: { final: RefineFinalResult; onClose: () => void }) {
    const color = final.outcome === "CRIT_SUCCESS" ? tokens.color.brass
                 : final.outcome === "SUCCESS" ? tokens.color.moss
                 : final.outcome === "FAIL" ? "#ada"
                 : tokens.color.burgundy;
    const symbol = final.outcome === "CRIT_SUCCESS" ? "✦✦"
                 : final.outcome === "SUCCESS" ? "✦"
                 : final.outcome === "FAIL" ? "✕"
                 : "☠";
    const label = final.outcome === "CRIT_SUCCESS" ? "대성공"
                 : final.outcome === "SUCCESS" ? "성공"
                 : final.outcome === "FAIL" ? "실패"
                 : "대실패";

    return (
        <div style={{ padding: "20px 4px", textAlign: "center" }}>
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                style={{ fontSize: 40, color, letterSpacing: 6, marginBottom: 10, textShadow: `0 0 20px ${color}66` }}
            >
                {symbol} {label}
            </motion.div>
            {final.destroyed ? (
                <>
                    <div style={{ fontSize: 16, color: tokens.color.burgundy, marginBottom: 6 }}>
                        장비가 부서졌습니다
                    </div>
                    <div style={{ fontSize: 13, opacity: 0.7 }}>
                        장인의 재 {final.artisanAshGained} 획득
                    </div>
                </>
            ) : (
                // 2026-04-25: 강화 수치 +N → +M 등장 애니 — 0.55→0.9s 로 연장. 음미할 시간 더.
                <div style={{ fontSize: 22, color: tokens.color.parchment, margin: "16px 0", display: "inline-flex", alignItems: "baseline", gap: 8, justifyContent: "center" }}>
                    <span style={{ opacity: 0.6 }}>+{final.enhancementBefore}</span>
                    <span style={{ opacity: 0.4, fontSize: 18 }}>→</span>
                    <motion.strong
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: [0.5, 1.35, 1], opacity: 1 }}
                        transition={{ duration: 0.9, delay: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                        style={{ color, fontSize: 36, textShadow: `0 0 24px ${color}AA, 0 0 8px ${color}` }}
                    >
                        +{final.enhancementAfter}
                    </motion.strong>
                </div>
            )}
            {final.overridenOutcome === "PITY" && (
                <div style={{ fontSize: 12, color: tokens.color.moss, opacity: 0.85, marginTop: 8, fontStyle: "italic" }}>
                    🕊 연민의 증표 3장이 대신 울었다
                </div>
            )}
            <div style={{ marginTop: 22 }}>
                <PrimaryButton onClick={onClose}>닫기</PrimaryButton>
            </div>
        </div>
    );
}

// ==================================================================
// 스타일
// ==================================================================

const overlayStyle: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
    position: "relative",
    background: tokens.color.ink,
    color: tokens.color.parchment,
    padding: "36px 48px 32px",
    borderRadius: 14,
    // 2026-04-25: 데스크톱 양쪽 여백 줄임 — 560 → 760. 모바일 96vw 까지 허용.
    width: 760,
    maxWidth: "96vw",
    maxHeight: "92vh",
    overflowY: "auto",
    border: `1px solid ${tokens.color.brass}44`,
    boxShadow: `0 24px 80px rgba(0,0,0,0.55), 0 0 32px ${tokens.color.brass}18`,
};

const closeButton: React.CSSProperties = {
    position: "absolute",
    top: 14,
    right: 14,
    width: 40,
    height: 40,
    borderRadius: 8,
    background: "transparent",
    border: `1px solid ${tokens.color.brass}33`,
    color: tokens.color.parchment,
    opacity: 0.7,
    cursor: "pointer",
    fontSize: 16,
};

const titleStyle: React.CSSProperties = {
    fontSize: 18,
    textAlign: "center",
    color: tokens.color.parchment,
    opacity: 0.88,
    margin: "0 0 8px 0",
    letterSpacing: 3,
    fontWeight: 500,
};

const loadingStyle: React.CSSProperties = {
    padding: "60px 0",
    textAlign: "center",
    opacity: 0.6,
    fontSize: 14,
    letterSpacing: 2,
};

const errorStyle: React.CSSProperties = {
    padding: "30px 10px",
    textAlign: "center",
    color: tokens.color.burgundy,
};

const stageStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 18,
    padding: "4px 0 0",
};

const itemBadgeWrap: React.CSSProperties = {
    position: "relative",
    marginTop: 6,
};

const itemBadge: React.CSSProperties = {
    width: 110,
    height: 110,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255,255,255,0.05), rgba(0,0,0,0.3))",
    border: `1px solid ${tokens.color.brass}55`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "box-shadow 0.6s ease",
};

const enhancementBadge: React.CSSProperties = {
    position: "absolute",
    bottom: -4,
    right: -6,
    minWidth: 42,
    padding: "4px 10px",
    borderRadius: 16,
    background: tokens.color.ink,
    border: `1px solid ${tokens.color.brass}`,
    color: tokens.color.brass,
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: 1,
    textAlign: "center",
    boxShadow: `0 2px 8px rgba(0,0,0,0.4)`,
};

const firstHintStyle: React.CSSProperties = {
    fontSize: 12,
    color: tokens.color.parchment,
    opacity: 0.48,
    letterSpacing: 4,
    marginTop: -4,
    marginBottom: -6,
};

const lastChanceHintStyle: React.CSSProperties = {
    fontSize: 12,
    color: tokens.color.burgundy,
    opacity: 0.92,
    letterSpacing: 3,
    marginTop: -4,
    marginBottom: -6,
    textShadow: `0 0 8px ${tokens.color.burgundy}55`,
};

const maxBeforeHintStyle: React.CSSProperties = {
    fontSize: 12,
    color: tokens.color.brass,
    opacity: 0.78,
    letterSpacing: 2,
    marginTop: -4,
    marginBottom: -6,
};

const slotsRow: React.CSSProperties = {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    padding: "4px 0",
};

const costLineStyle: React.CSSProperties = {
    display: "flex",
    gap: 10,
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    opacity: 0.78,
    letterSpacing: 1,
};

const actionColumn: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "100%",
    maxWidth: 260,
    alignItems: "stretch",
};

const ghostButton: React.CSSProperties = {
    padding: "10px 16px",
    background: "transparent",
    color: tokens.color.parchment,
    border: `1px solid ${tokens.color.brass}44`,
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 14,
    letterSpacing: 1,
    opacity: 0.82,
};
